import { LanguagesEnum } from '@/api/enums/languages.enum';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  CourseContentType,
  CourseFormState,
  Slide,
  SlideDeck,
  SlideTemplateType,
  SlideType as SlideTypeEnum,
} from '@/client/courses/types/courses.type';
import { UnsavedChangesModal } from '@/components/courses/modals/UnsavedChangesModal';
import { SlideTemplate } from '@/components/slides/admin/slide-templates/SlideTemplate';
import { SlideDeckSidebar } from '@/components/slides/admin/SlideDeckSidebar';
import { SlideTimelineCard } from '@/components/slides/admin/SlideTimelineCard';
import { SlidesNavigation } from '@/components/slides/SlidesNavigation';
import { useToast } from '@/hooks/useToast';
import { EditableText } from '@/ui/editable-text/EditableText';
import { FlexContainer } from '@/ui/styled-ui';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { AxiosError } from 'axios';
import { Dialog, DialogProps } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 } from 'uuid';

const StyledDialog = styled(Dialog)`
  width: 100%;
  height: 100%;
  max-height: 95vh;
  max-width: 1500px;
`;

const StyledModalHeader = styled.h1`
  font-size: var(--heading-medium-font-size) !important;
  color: var(--black-main);
`;

const ModalLayout = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100% - 60px);
`;

const MainContent = styled.div<{ isPurpleBackground?: boolean }>`
  flex: 1;
  padding: 32px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--purple-dark)' : 'var(--beige-main)'};
  color: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  border: 1px solid var(--gray-dark);
  border-right: none;
  border-top-left-radius: var(--default-border-radius);
  border-bottom-left-radius: var(--default-border-radius);
  padding-top: 52px;
  width: 1000px;

  h1 {
    margin-bottom: var(--medium-padding);
  }
`;

const AddSlideCard = styled(FlexContainer)`
  width: auto;
  background: var(--white-main);
  border-radius: var(--xsmall-border-radius);
  padding: var(--medium-padding) var(--small-padding);
  border: 2px solid var(--gray-dark);
  color: var(--black-main);
  cursor: pointer;
  font-size: var(--xsmall-font-size);
  box-shadow: none;
  margin-right: 0;
  min-height: 150px;
  margin-bottom: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--gray-lighter);
  }

  .add-icon {
    display: flex;
    font-size: var(--medium-font-size);
    line-height: 1;
  }
`;

const TimelineContainer = styled.div`
  padding: var(--default-padding) 0 var(--medium-padding) 0;
  background: var(--white-main);

  > div {
    border-radius: var(--medium-border-radius);
    border: 1px solid var(--gray-dark);
    padding: 48px;
  }
`;

const TimelineLabel = styled.div`
  font-weight: 500;
  margin-bottom: var(--xsmall-padding);
`;

type ContentSlideDeckModalProps = {
  index: number;
  values: CourseFormState;
  setFieldValue: (field: string, value: any) => void;
  visible: boolean;
  onHide: () => void;
} & DialogProps;

export const ContentSlideDeckModal: React.FC<ContentSlideDeckModalProps> = ({
  visible,
  onHide,
  index,
  values,
  setFieldValue,
  ...dialogProps
}) => {
  const { id: courseId } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  const [selectedLanguage, setSelectedLanguage] = useState(
    values?.courseLanguages?.length === 1
      ? values?.courseLanguages?.[0]
      : LanguagesEnum.EN,
  );
  const [slidesByLanguage, setSlidesByLanguage] = useState<
    Record<string, Slide[]>
  >({});
  const [tempSlidesByLanguage, setTempSlidesByLanguage] = useState<
    Record<string, Slide[]>
  >({});

  const initializeSlidesForLanguage = (currentLanguage: string) => {
    if (!slidesByLanguage[currentLanguage]) {
      const presentation = values?.courseContents[index]?.presentation;
      const hasTranslations =
        presentation?.translations?.length &&
        presentation.translations.length > 0;
      const existingSlides = presentation?.translations?.find(
        ({ language }) => language === currentLanguage,
      )?.slides;

      const defaultSlides =
        hasTranslations && existingSlides
          ? existingSlides.map((s) => ({
              ...s,
              id: s.id || `temp-${v4()}`,
              infobox: s.infobox || '',
              headline: s.headline || '',
              html: s.html || '',
              popupSlide: s.popupSlide || undefined,
              templateType: s.templateType || SlideTemplateType.TEXT_IMAGE,
              footnote: s.footnote || '',
              images: s.images || undefined,
              interactiveElement: s.interactiveElement || undefined,
              type: s.type || undefined,
              position: s.position || undefined,
            }))
          : [
              {
                id: `temp-${v4()}`,
                headline: t('content.slide', { number: 1 }),
                html: '',
                popupSlide: undefined,
                templateType: SlideTemplateType.TEXT_IMAGE,
                footnote: '',
                images: [
                  {
                    file: null,
                    position: 0,
                    withBorder: true,
                    withZoom: false,
                  },
                ],
                interactiveElement: undefined,
                type: SlideTypeEnum.NORMAL,
                position: 0,
                infobox: '',
              },
            ];

      setSlidesByLanguage((prev) => ({
        ...prev,
        [currentLanguage]: defaultSlides,
      }));
    }
  };

  const [loadingSlides, setLoadingSlides] = useState(false);
  useEffect(() => {
    const fetchPresentation = async () => {
      setLoadingSlides(true);

      const presentation = values?.courseContents[index]?.presentation;
      const presentationId = presentation?.id;

      if (visible && presentationId && !presentationId.startsWith('temp')) {
        try {
          const fetched: SlideDeck = await client.courses.getSlideDeck(
            courseId || '',
            presentationId,
          );

          // Populate slidesByLanguage from fetched presentation
          if (fetched?.translations && fetched?.translations?.length > 0) {
            const newSlidesByLanguage: Record<string, Slide[]> = {};

            for (const translation of fetched.translations) {
              newSlidesByLanguage[translation.language] = translation.slides
                .map((slide: Slide) => ({
                  ...slide,
                  id: slide.id || `temp-${v4()}`,
                  infobox: slide.infobox || '',
                  headline: slide.headline || '',
                  html: slide.html || '',
                  popupSlide: slide.popupSlide || undefined,
                  templateType:
                    slide.templateType || SlideTemplateType.TEXT_IMAGE,
                  footnote: slide.footnote || '',
                  images: slide.images || undefined,
                  type: slide.type || undefined,
                  interactiveElement: slide.interactiveElement || undefined,
                }))
                .sort((a, b) => (a.position || 0) - (b.position || 0));
            }

            setSlidesByLanguage(newSlidesByLanguage);
            setTempSlidesByLanguage(newSlidesByLanguage);
          }
        } catch (error) {
          handleAxiosError(error as AxiosError, toast);
        }
      } else {
        initializeSlidesForLanguage(selectedLanguage);
      }

      setLoadingSlides(false);
    };

    if (visible) {
      setPresentationTitle(
        values?.courseContents[index]?.presentation?.title ||
          t('content.new.slidedeck'),
      );
    }

    fetchPresentation();
  }, [visible, values?.id, index]);

  const slides = slidesByLanguage[selectedLanguage] || [];
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [isAnyDragging, setIsAnyDragging] = useState(false);
  const [isEditingPopUpSlide, setIsEditingPopUpSlide] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isPurpleBackground, setIsPurpleBackground] = useState(
    slides[currentSlideIdx]?.templateType === SlideTemplateType.SUMMARY,
  );

  const [presentationTitle, setPresentationTitle] = useState(
    values?.courseContents[index]?.presentation?.title ||
      t('content.new.slidedeck'),
  );

  const handleLanguageChange = (language: LanguagesEnum) => {
    setSelectedLanguage(language);
    initializeSlidesForLanguage(language);
    setCurrentSlideIdx(0);
    setSelectedSlideId(null);
    setIsEditingPopUpSlide(false);
  };

  const mainSlides = slides.filter(({ type }) => type === SlideTypeEnum.NORMAL);
  const popUpSlides = slides.filter(({ type }) => type === SlideTypeEnum.POPUP);

  const selectedSlide = selectedSlideId
    ? slides.find(({ id }) => id === selectedSlideId)
    : null;

  const currentSlideData = useMemo(() => {
    if (isEditingPopUpSlide && selectedSlide) {
      return selectedSlide;
    }
    return mainSlides[currentSlideIdx] || {};
  }, [
    isEditingPopUpSlide,
    selectedSlide,
    mainSlides,
    currentSlideIdx,
    selectedLanguage,
  ]);

  useEffect(() => {
    if (currentSlideIdx >= mainSlides.length && mainSlides.length > 0) {
      setCurrentSlideIdx(mainSlides.length - 1);
    }
  }, [mainSlides.length, currentSlideIdx]);

  useEffect(() => {
    if (isEditingPopUpSlide && selectedSlide) {
      setIsPurpleBackground(
        popUpSlides[currentSlideIdx]?.templateType ===
          SlideTemplateType.SUMMARY,
      );
    } else {
      setIsPurpleBackground(
        mainSlides[currentSlideIdx]?.templateType === SlideTemplateType.SUMMARY,
      );
    }
  }, [currentSlideIdx, slides, isEditingPopUpSlide, selectedSlide]);

  const addSlide = (isPopUp = false) => {
    const newSlide = {
      id: `temp-${v4()}`,
      headline: isPopUp
        ? t('content.popup.slide', { number: popUpSlides.length + 1 })
        : t('content.slide', { number: mainSlides.length + 1 }),
      html: '',
      tempId: isPopUp ? v4() : undefined,
      popupSlide: undefined,
      templateType: SlideTemplateType.TEXT_IMAGE,
      footnote: '',
      images: [{ file: null, position: 0, withBorder: true, withZoom: false }],
      type: isPopUp ? SlideTypeEnum.POPUP : SlideTypeEnum.NORMAL,
      position: mainSlides.length + 1,
      infobox: '',
    };

    const updatedSlides = [...slides, newSlide];
    setSlidesByLanguage((prev) => ({
      ...prev,
      [selectedLanguage]: updatedSlides,
    }));
  };

  const deleteSlide = (selectedSlideId?: string) => {
    const slideToDelete = slides.find(({ id }) => id === selectedSlideId);
    if (!slideToDelete) return;

    const isPopUp = slideToDelete.type === SlideTypeEnum.POPUP;
    let filteredSlides = slides.filter(({ id }) => id !== selectedSlideId);

    if (
      !isPopUp &&
      filteredSlides.filter((s) => s.type !== SlideTypeEnum.POPUP).length === 0
    ) {
      return;
    }

    if (isPopUp) {
      const deletedId = slideToDelete.id;
      const deletedTempId = slideToDelete.tempId;

      filteredSlides = filteredSlides.map((slide) => {
        const refsDeleted =
          (deletedId &&
            (typeof slide.popupSlide === 'string'
              ? slide.popupSlide === deletedId
              : slide.popupSlide?.id === deletedId)) ||
          (deletedTempId && slide.tempPopupSlideId === deletedTempId);

        return refsDeleted
          ? { ...slide, popupSlide: undefined, tempPopupSlideId: undefined }
          : slide;
      });
    }

    setSlidesByLanguage((prev) => ({
      ...prev,
      [selectedLanguage]: filteredSlides,
    }));

    if (!isPopUp && currentSlideIdx >= filteredSlides.length) {
      setCurrentSlideIdx(filteredSlides.length - 1);
    }

    if (selectedSlideId === selectedSlide?.id) {
      setSelectedSlideId(null);
      setIsEditingPopUpSlide(false);
    }
  };

  const updateSlide = (selectedSlideId: string, updates: Partial<Slide>) => {
    const aiFields: (keyof Slide)[] = [
      'aiCustomizationInstructions',
      'isAiCustomizationEnabled',
    ];
    const hasAiFieldUpdates = aiFields.some((field) => field in updates);

    if (hasAiFieldUpdates) {
      const currentSlide = slides.find((s) => s.id === selectedSlideId);
      const slidePosition = currentSlide?.position;
      const aiUpdates = Object.fromEntries(
        Object.entries(updates).filter(([key]) =>
          aiFields.includes(key as keyof Slide),
        ),
      );

      setSlidesByLanguage((prev) => {
        const updated = { ...prev };
        for (const lang of Object.keys(updated)) {
          updated[lang] = updated[lang].map((slide) => {
            if (lang === selectedLanguage && slide.id === selectedSlideId) {
              return { ...slide, ...updates };
            }
            if (lang !== selectedLanguage && slide.position === slidePosition) {
              return { ...slide, ...aiUpdates };
            }
            return slide;
          });
        }
        return updated;
      });
    } else {
      const updatedSlides = slides.map((slide) =>
        slide.id === selectedSlideId ? { ...slide, ...updates } : slide,
      );
      setSlidesByLanguage((prev) => ({
        ...prev,
        [selectedLanguage]: updatedSlides,
      }));
    }
  };

  const handlePrevSlide = () => {
    if (currentSlideIdx > 0) setCurrentSlideIdx(currentSlideIdx - 1);
  };

  const handleNextSlide = () => {
    if (currentSlideIdx < mainSlides.length - 1)
      setCurrentSlideIdx(currentSlideIdx + 1);
  };

  const handleSlidesDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsAnyDragging(false);

    if (over && active.id !== over.id) {
      const from = slides.findIndex((s) => s.id === active.id);
      const to = slides.findIndex((s) => s.id === over.id);

      if (from !== -1 && to !== -1) {
        const currentSlideId = slides[currentSlideIdx]?.id;

        setSlidesByLanguage((prev) => {
          const reorderedSlides = arrayMove(
            prev[selectedLanguage] || [],
            from,
            to,
          );
          const updatedSlides = reorderedSlides.map((slide, index) => ({
            ...slide,
            position: index,
          }));

          if (currentSlideId) {
            const newIndex = updatedSlides.findIndex(
              (s) => s.id === currentSlideId,
            );
            if (newIndex !== -1) {
              setCurrentSlideIdx(newIndex);
            }
          }

          return {
            ...prev,
            [selectedLanguage]: updatedSlides,
          };
        });
      }
    }
  };

  const handleSlidesDragStart = () => {
    setIsAnyDragging(true);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const getCurrentEditingSlide = () => {
    if (isEditingPopUpSlide && selectedSlide) {
      return selectedSlide;
    }
    return currentSlideData;
  };

  const currentEditingSlide = getCurrentEditingSlide();

  const handleSave = async () => {
    if (!courseId) return;

    const presentationProps = {
      id: values?.courseContents[index]?.presentation?.id?.includes('temp-')
        ? undefined
        : values?.courseContents[index]?.presentation?.id,
      courseId,
      title: presentationTitle,
    };

    const allTranslations = Object.entries(slidesByLanguage).map(
      ([language, slides]) => ({
        language,
        slides: slides.map((slide, index) => ({
          ...slide,
          interactiveElement:
            slide.templateType === SlideTemplateType.GALLERY
              ? null
              : slide.interactiveElement,
          id: slide.id?.includes('temp-') ? undefined : slide.id,
          templateType: slide.templateType || SlideTemplateType.TEXT_IMAGE,
          images: slide.images
            ?.filter((image) => image.file !== null && image.file?.id !== null)
            ?.filter(
              (_, index) =>
                slide.templateType === SlideTemplateType.GALLERY || index === 0,
            )
            ?.map((image) => ({
              file: image.file?.id || null,
              position: image.position,
              title: image.title || '',
              description: image.description || '',
              withBorder: image.withBorder || false,
              withZoom: image.withZoom || false,
            })),
          html: slide.html || '',
          position: index,
          popupSlide:
            (typeof slide.popupSlide === 'string'
              ? slide.popupSlide
              : slide.popupSlide?.id) || undefined,
        })),
      }),
    );

    const payload = {
      ...presentationProps,
      translations: allTranslations,
    };

    try {
      const service = client.courses;
      let response;

      if (payload.id && !payload.id.includes('temp-')) {
        response = await service.updateSlideDeck(
          courseId,
          payload.id,
          payload as SlideDeck,
        );
      } else {
        response = await service.createSlideDeck(
          courseId,
          payload as SlideDeck,
        );
      }

      const updatedContents = [...values.courseContents];
      updatedContents[index] = {
        ...updatedContents[index],
        type: CourseContentType.PRESENTATION,
        presentation: response,
        position: index,
      };

      setFieldValue('courseContents', updatedContents);
      toast?.success(t('toast.success'), t('content.slides.save.success'));

      setTempSlidesByLanguage(slidesByLanguage);
      onHide();
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    }
  };

  const handleCopyToLanguage = (targetLanguages: LanguagesEnum[]) => {
    targetLanguages.forEach((targetLanguage) => {
      const currentSlides = slidesByLanguage[selectedLanguage] || [];

      const popupIdMapping: Record<string, string> = {};

      const copiedSlides = currentSlides.map((slide) => {
        const newId = `temp-${v4()}`;
        const newTempId = v4();

        if (slide.type === SlideTypeEnum.POPUP) {
          const oldId = slide.tempId || slide.id;

          if (oldId) {
            popupIdMapping[oldId] = newTempId;
          }
        }

        return {
          ...slide,
          id: newId,
          tempId: newTempId,
          position: slide.position || 0,
        };
      });

      // Update popup slide references using the mapping
      const updatedCopiedSlides = copiedSlides.map((slide) => {
        const updatedSlide = { ...slide };

        if (slide.popupSlide) {
          const oldPopupId =
            typeof slide.popupSlide === 'string'
              ? slide.popupSlide
              : slide.popupSlide.id;

          if (oldPopupId) {
            updatedSlide.tempPopupSlideId = popupIdMapping[oldPopupId];
            updatedSlide.popupSlide = undefined;
          }
        } else if (slide.tempPopupSlideId) {
          updatedSlide.tempPopupSlideId =
            popupIdMapping[slide.tempPopupSlideId];
        }

        return updatedSlide;
      });

      setSlidesByLanguage((prev) => ({
        ...prev,
        [targetLanguage]: updatedCopiedSlides,
      }));
    });

    toast?.success(t('toast.success'), t('content.all.slides.copied.success'));
  };

  const dialogHeaderTemplate = () => (
    <FlexContainer wrap="wrap">
      <StyledModalHeader className="flex-1 w-full">
        <EditableText
          key={presentationTitle}
          value={presentationTitle}
          saveValue={(value: string) => {
            if (value !== presentationTitle) {
              setFieldValue(`courseContents.${index}.isAutoNamed`, false);
            }
            setPresentationTitle(value);
          }}
        />
      </StyledModalHeader>
    </FlexContainer>
  );

  return (
    <StyledDialog
      visible={visible}
      onHide={() => {
        if (
          JSON.stringify(slidesByLanguage) !==
          JSON.stringify(tempSlidesByLanguage)
        ) {
          setShowUnsavedChangesModal(true);
        } else {
          onHide();
        }
      }}
      header={dialogHeaderTemplate}
      draggable={false}
      blockScroll
      {...dialogProps}
      closeOnEscape={false}
    >
      {loadingSlides ? (
        <FlexContainer
          justify="center"
          align="center"
          style={{ minHeight: 300 }}
        >
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          <ModalLayout>
            <MainContent isPurpleBackground={isPurpleBackground}>
              <SlideTemplate
                key={`${selectedLanguage}-${
                  currentEditingSlide?.id
                }-${currentSlideIdx}-${
                  isEditingPopUpSlide ? 'popup' : 'main'
                }-${currentSlideData?.templateType || 'text-image'}`}
                currentEditingSlide={currentEditingSlide}
                updateSlide={updateSlide}
                selectedLanguage={selectedLanguage}
                isEditingPopUpSlide={isEditingPopUpSlide}
                currentSlideIdx={currentSlideIdx}
                slides={slides}
                currentSlideData={currentSlideData}
                totalSlides={mainSlides.length}
              />
              <SlidesNavigation
                currentSlideIdx={currentSlideIdx}
                totalSlides={mainSlides.length}
                isEditingPopUpSlide={isEditingPopUpSlide}
                isEditMode
                attemptLanguage={selectedLanguage}
                onPrevSlide={handlePrevSlide}
                onNextSlide={handleNextSlide}
              />
            </MainContent>

            <SlideDeckSidebar
              selectedLanguage={selectedLanguage}
              courseLanguages={values.courseLanguages || []}
              currentSlideData={currentSlideData}
              popUpSlides={popUpSlides}
              isEditingPopUpSlide={isEditingPopUpSlide}
              isCustomContent={!!values.customAccount}
              onLanguageChange={handleLanguageChange}
              onSlideUpdate={updateSlide}
              onCopyToLanguage={handleCopyToLanguage}
              onSave={handleSave}
            />
          </ModalLayout>

          {/* Slides timeline */}
          <TimelineContainer>
            <div>
              <TimelineLabel>{t('content.slides.timeline')}</TimelineLabel>
              <DndContext
                modifiers={[restrictToHorizontalAxis]}
                sensors={sensors}
                onDragStart={handleSlidesDragStart}
                onDragEnd={handleSlidesDragEnd}
              >
                <SortableContext
                  items={mainSlides
                    .map((s) => s.id)
                    .filter((id): id is string => id !== undefined)}
                  strategy={horizontalListSortingStrategy}
                >
                  <FlexContainer
                    gap={8}
                    justify="flex-start"
                    align="stretch"
                    style={{ marginBottom: 24, overflowX: 'auto' }}
                  >
                    {mainSlides.map((slide, index) => (
                      <SlideTimelineCard
                        key={slide.id}
                        slide={slide}
                        index={index}
                        isSelected={
                          currentSlideIdx === index && !isEditingPopUpSlide
                        }
                        isSortable={true}
                        onSelect={() => {
                          setCurrentSlideIdx(index);
                          setIsEditingPopUpSlide(false);
                          setSelectedSlideId(null);
                        }}
                        onDelete={() => deleteSlide(slide.id)}
                        isAnyDragging={isAnyDragging}
                      />
                    ))}

                    <AddSlideCard
                      direction="column"
                      justify="center"
                      align="center"
                      onClick={() => addSlide(false)}
                    >
                      <div className="add-icon">+</div>
                      {t('content.add.slide')}
                    </AddSlideCard>
                  </FlexContainer>
                </SortableContext>
              </DndContext>

              <TimelineLabel>{t('content.popup.slides')}</TimelineLabel>
              <FlexContainer
                gap={8}
                justify="flex-start"
                align="stretch"
                style={{ overflowX: 'auto' }}
              >
                {popUpSlides.map((slide, index) => (
                  <SlideTimelineCard
                    key={slide.id}
                    slide={slide}
                    index={index}
                    isSelected={
                      selectedSlideId === slide.id && isEditingPopUpSlide
                    }
                    isSortable={false}
                    onSelect={() => {
                      if (slide.id) {
                        setSelectedSlideId(slide.id);
                        setIsEditingPopUpSlide(true);
                      }
                    }}
                    onDelete={() => deleteSlide(slide.id || '')}
                    isAnyDragging={isAnyDragging}
                  />
                ))}
                <AddSlideCard
                  direction="column"
                  justify="center"
                  align="center"
                  onClick={() => addSlide(true)}
                >
                  <div className="add-icon">+</div>
                  {t('content.add.slide')}
                </AddSlideCard>
              </FlexContainer>
            </div>
          </TimelineContainer>
        </>
      )}

      <UnsavedChangesModal
        visible={showUnsavedChangesModal}
        message={t('content.quiz.unsavedChanges.confirm')}
        onHide={() => setShowUnsavedChangesModal(false)}
        onSave={handleSave}
        onDiscard={() => {
          setSlidesByLanguage(tempSlidesByLanguage);
          onHide();
        }}
      />
    </StyledDialog>
  );
};
